import {
  type TerminalClientMessage,
  type TerminalEventPayload,
  WebSocketEntityType,
} from '@this+that/models';

import { classifyTerminalError } from './classifyTerminalError';
import {
  type TerminalGateway,
  TerminalSendError,
  type TerminalUnsubscribe,
} from './types';
import type { TerminalRequests } from '../../../services/terminal.service';

export interface TerminalSocketBridge {
  subscribe: (
    listener: (payload: TerminalEventPayload) => void,
  ) => TerminalUnsubscribe;
  isConnected: () => boolean;
  subscribeState: (
    listener: (isConnected: boolean) => void,
  ) => TerminalUnsubscribe;
}

export const TERMINAL_EVENT_ENTITY = WebSocketEntityType.TerminalEvent;

export function createTerminalGateway(
  requests: TerminalRequests,
  socket: TerminalSocketBridge,
): TerminalGateway {
  return {
    listDevices: async () => {
      const devices = await requests.getTerminalDevices();
      return devices.map((device) => ({
        deviceId: device.deviceId,
        name: device.name,
        isConnected: device.isConnected === true,
        lastSeenAt: device.lastSeenAt,
      }));
    },
    createTicket: requests.createTerminalTicket,
    createAttachmentUpload: async (deviceId, file) => {
      const ticket = await requests.createTerminalAttachmentUpload({
        deviceId,
        fileName: file.name,
        contentType: file.contentType,
        size: file.size,
      });
      return {
        attachmentId: ticket.attachmentId,
        uploadUrl: ticket.uploadUrl,
        uploadFields: JSON.parse(ticket.uploadFields) as Record<string, string>,
        downloadUrl: ticket.downloadUrl,
      };
    },
    sendMessage: async (deviceId, message: TerminalClientMessage) => {
      try {
        await requests.sendTerminalMessage(deviceId, JSON.stringify(message));
      } catch (error) {
        const sendError = new TerminalSendError(classifyTerminalError(error));
        sendError.cause = error;
        throw sendError;
      }
    },
    subscribeEvents: socket.subscribe,
    isSocketConnected: socket.isConnected,
    subscribeSocketState: socket.subscribeState,
  };
}
